import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const TensileReportDetail: React.FC<{
  heatNumber: string;
  pipeLengthM?: number;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, pipeLengthM = 12.18, metadata, projectLimitFields = [], frequency, measurements = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const [activeSpecimenId, setActiveSpecimenId] = useState<string | null>(null);
  const tensileMeasurements = measurements.filter((measurement) => measurement.reportType === 'TENSILE');
  const activeMeasurement = tensileMeasurements.find((measurement) => measurement.id === activeSpecimenId) || tensileMeasurements[0];
  const tensileLocationMarks = tensileMeasurements.map((measurement, index) => {
    const location = (measurement.tensileLocation || '').toLowerCase();
    const isWeld = location.includes('weld');
    const isHaz = location.includes('haz');
    return {
      id: measurement.tensileSpecimenId || measurement.fieldSymbol || `S${index + 1}`,
      label: `${measurement.tensileSpecimenId || measurement.fieldSymbol || `S${index + 1}`} ${formatLabel(measurement.tensileLocation || measurement.fieldLabel)}`,
      position: isWeld || isHaz ? 0.5 : Math.min(0.85, 0.2 + index * 0.18),
      status: measurement.status,
      below: isHaz,
    };
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Specimens</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">{tensileMeasurements.length} saved tensile specimens</div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('TENSILE')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Item
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {tensileMeasurements.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[#E2E5EA] bg-white px-3 py-2 text-[12px] text-heritage-grey">
              No specimens added yet.
            </div>
          ) : tensileMeasurements.map((measurement) => (
            <button
              key={measurement.id}
              type="button"
              onClick={() => setActiveSpecimenId(measurement.id)}
              className={cn(
                'rounded-lg border px-3 py-2 text-[12px] font-semibold',
                activeMeasurement?.id === measurement.id ? 'border-hero-red-20 bg-hero-red-10 text-readable-red' : 'border-[#E2E5EA] bg-white text-blueprint-blue'
              )}
            >
              {measurement.tensileSpecimenId || measurement.fieldSymbol || measurement.fieldLabel}
            </button>
          ))}
        </div>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            {activeMeasurement ? (
              <>
                <SectionHead
                  title={activeMeasurement.tensileSpecimenId || activeMeasurement.fieldLabel}
                  subtitle={`${formatLabel(activeMeasurement.tensileLocation || 'user entry')} / ${activeMeasurement.tensileOrientation || '-'} / A = ${activeMeasurement.tensileAreaMm2 || '-'} mm2`}
                  status={activeMeasurement.status}
                />
                <TensileResultRow label="Yield strength" formula="Project limit" value={Number(activeMeasurement.tensileYsMpa || (activeMeasurement.fieldPath === 'ys_mpa' ? activeMeasurement.measuredValue : 0)) || 0} unit="MPa" min={300} max={700} lo={getProjectLimitMin(projectLimitFields, 'ys_mpa', 0)} status={activeMeasurement.status} />
                <TensileResultRow label="Tensile strength" formula="Project limit" value={Number(activeMeasurement.tensileUtsMpa || (activeMeasurement.fieldPath === 'uts_mpa' ? activeMeasurement.measuredValue : 0)) || 0} unit="MPa" min={400} max={850} lo={getProjectLimitMin(projectLimitFields, 'uts_mpa', 0)} hi={getProjectLimitMax(projectLimitFields, 'uts_mpa', 850)} status={activeMeasurement.status} />
                <TensileResultRow label="Elongation" formula="Project limit" value={Number(activeMeasurement.tensileElongationPct || (activeMeasurement.fieldPath === 'elongation_pct' ? activeMeasurement.measuredValue : 0)) || 0} unit="%" min={0} max={50} lo={getProjectLimitMin(projectLimitFields, 'elongation_pct', 0)} status={activeMeasurement.status} />
                <TensileResultRow label="YS/UTS ratio" formula="Project limit" value={Number(activeMeasurement.tensileYsUtsRatio || (activeMeasurement.fieldPath === 'ys_uts_ratio' ? activeMeasurement.measuredValue : 0)) || 0} unit="" min={0} max={1} hi={getProjectLimitMax(projectLimitFields, 'ys_uts_ratio', 1)} status={activeMeasurement.status} />
                <div className="border-t border-[#E2E5EA] bg-page-bg px-4 py-3 text-[11px] text-heritage-grey">
                  Source: <span className="font-mono text-text-grey">{activeMeasurement.source || 'manual entry'}</span>
                  <span className="mx-2">-</span>
                  Frequency: <span className="font-mono text-text-grey">{activeMeasurement.frequency || metadata?.frequency || frequency || '-'}</span>
                </div>
              </>
            ) : (
              <div className="p-4 text-[13px] text-heritage-grey">
                No tensile specimens added yet. Click Add Item to enter values manually.
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead
              title="All Specimens"
              subtitle={`${tensileMeasurements.length} user-added items`}
              right={(
                <Button type="button" size="sm" onClick={() => onAddMeasurement?.('TENSILE')} className="bg-hero-red text-white hover:bg-hero-red-60">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Item
                </Button>
              )}
            />
            {tensileMeasurements.length === 0 ? (
              <div className="p-4 text-[13px] text-heritage-grey">No tensile rows saved yet.</div>
            ) : (
              <table className="w-full text-left">
                <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                  <tr>
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Orient.</th>
                    <th className="px-4 py-3 text-right">Area mm2</th>
                    <th className="px-4 py-3 text-right">YS MPa</th>
                    <th className="px-4 py-3 text-right">UTS MPa</th>
                    <th className="px-4 py-3 text-right">Elong %</th>
                    <th className="px-4 py-3 text-right">YS/UTS</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                  {tensileMeasurements.map((measurement) => (
                    <tr key={measurement.id} onClick={() => setActiveSpecimenId(measurement.id)} className={cn('cursor-pointer', activeMeasurement?.id === measurement.id && 'bg-[#E7F3FF]')}>
                      <td className="px-4 py-3 font-mono font-semibold">{measurement.tensileSpecimenId || measurement.fieldSymbol || 'ITEM'}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-blueprint-blue">{formatLabel(measurement.tensileLocation || measurement.fieldLabel)}</div>
                        {/* <div className="mt-1 font-mono text-[10px] text-heritage-grey">{measurement.fieldPath}</div> */}
                      </td>
                      <td className="px-4 py-3">{measurement.tensileOrientation || '-'}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.tensileAreaMm2 || '-'}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.tensileYsMpa || (measurement.fieldPath === 'ys_mpa' ? measurement.measuredValue : '-')}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.tensileUtsMpa || (measurement.fieldPath === 'uts_mpa' ? measurement.measuredValue : '-')}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.tensileElongationPct || (measurement.fieldPath === 'elongation_pct' ? measurement.measuredValue : '-')}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.tensileYsUtsRatio || (measurement.fieldPath === 'ys_uts_ratio' ? measurement.measuredValue : '-')}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={measurement.status} />
                        {/* <div className="mt-1 text-[10px] text-heritage-grey">{measurement.limit}</div> */}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ItemActionButtons measurement={measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Specimen Locations" />
            <PipeLocationDiagram
              lengthM={pipeLengthM}
              marks={tensileLocationMarks}
            />
          </CardContent>
        </Card>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber || '-', true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={getProjectLimitRows(projectLimitFields, [
          ['No project limits', 'Configure project limits to show live values', true],
        ])}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows
              rows={[
                ['Frequency', metadata?.frequency || frequency || '-', true],
                ['Heat number', heatNumber || '-', true],
                ['Specimens', `${tensileMeasurements.length} saved`, true],
              ]}
            />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};
